import React from 'react';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import type { Project, QuestionnaireField } from '../types/questionnaires';

interface ProjectCardProps {
  project: Project;
  projectNumber: number;
  totalProjects: number;
  templateFields: QuestionnaireField[];  // NEW: Template fields instead of mode
  isReadOnly: boolean;
  onChange: (updates: Partial<Project>) => void;
  onRemove: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  projectNumber,
  totalProjects,
  templateFields,
  isReadOnly,
  onChange,
  onRemove
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  const handleRemove = () => {
    const confirmed = window.confirm(
      `Möchten Sie Projekt ${projectNumber} wirklich entfernen?\n\n` +
      `Alle eingegebenen Daten für dieses Projekt gehen verloren.`
    );
    
    if (confirmed) {
      onRemove();
    }
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    onChange({
      fieldValues: {
        ...(project.fieldValues || {}),
        [fieldId]: value
      }
    });
  };

  // Sort fields by order
  const sortedFields = [...templateFields].sort((a, b) => a.order - b.order);

  // Check if project has any data
  const hasData = project.fieldValues && Object.values(project.fieldValues).some(v => v?.trim());

  // Get project title for display (first field value or generic)
  const projectTitle = project.fieldValues?.['projektTitel'] || 
                       Object.values(project.fieldValues || {})[0] || 
                       '';

  return (
    <div className="bg-white border-2 border-purple-200 rounded-xl shadow-lg overflow-hidden">
      {/* Project Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="hover:bg-purple-500 rounded-lg p-1 transition-colors"
              title={isExpanded ? 'Einklappen' : 'Aufklappen'}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            
            <div>
              <h3 className="text-xl font-bold">Projekt {projectNumber}</h3>
              {projectTitle && (
                <p className="text-purple-100 text-sm mt-1">{projectTitle}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {hasData && (
              <span className="bg-green-400 text-green-900 px-3 py-1 rounded-full text-xs font-medium">
                Daten vorhanden
              </span>
            )}
            
            {!isReadOnly && totalProjects > 1 && (
              <button
                onClick={handleRemove}
                className="hover:bg-red-500 bg-red-600 text-white rounded-lg px-3 py-2 transition-colors flex items-center gap-2 text-sm font-medium"
                title="Projekt entfernen"
              >
                <Trash2 className="w-4 h-4" />
                Entfernen
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Project Content (Collapsible) */}
      {isExpanded && (
        <div className="p-6">
          <div className="space-y-4">
            {sortedFields.map(field => (
              <div key={field.id}>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                
                {field.type === 'text' ? (
                  <input
                    type="text"
                    value={project.fieldValues?.[field.id] || ''}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    disabled={isReadOnly}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-600"
                  />
                ) : (
                  <textarea
                    value={project.fieldValues?.[field.id] || ''}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder={field.placeholder}
                    rows={4}
                    disabled={isReadOnly}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-600"
                  />
                )}
                
                {field.helpText && (
                  <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {!isExpanded && hasData && (
        <div className="px-6 py-3 text-sm text-gray-600 border-t border-gray-200">
          Projekt ausgefüllt - Klicken Sie oben zum Bearbeiten
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
